import selector from 'utils/selector'
import published from 'utils/published'
import {ProblemSolution, Problem, Optional} from 'data/types'
import services from 'services'
import APIService, {SolutionInput} from './APIService'
import {Cache} from 'utils'

export default class SingleProblemService {
  public problemId: number
  public problem = published<Optional<Problem>>(null)
  public solutions = published<Array<ProblemSolution>>([])

  public constructor(problemId: string) {
    this.problemId = parseInt(problemId, 10)
    this.loadSolutions()
    this.loadProblem()
  }

  private loadProblem = async ({triggerRefresh = false, withLoader = true} = {}) => {
    if (withLoader) {
      this.problem.startLoading()
    }
    const response = await APIService.getProblem(this.problemId)
    if (response.ok) {
      this.problem.publish(response.body)
      if (triggerRefresh) {
        services.getDispatchService().dispatch('problemUpdated', response.body)
      }
    }
    this.problem.endLoading()
  }

  public loadSolutions = async (withLoader = true) => {
    if (this.solutions.isLoading) {
      return
    }
    if (withLoader) {
      this.solutions.startLoading()
    }

    const result = await APIService.getSolutionsForProblem(this.problemId)
    if (result.ok) {
      this.solutions.publish(result.body ?? [])
    }
    this.solutions.endLoading()
  }

  public createSolution = async (input: SolutionInput) => {
    const response = await APIService.createSolutionForProblem(this.problemId, input)
    if (response.ok) {
      this.solutions.reduce((items) => items.concat(response.body))
      await this.loadProblem({triggerRefresh: true, withLoader: false})
    }
  }

  public archiveSolution = async (solutionId: number) => {
    const response = await APIService.archiveSolution(solutionId)
    if (response.ok) {
      this.solutions.reduce((items) => items.filter((s) => s.id !== solutionId))
      await this.loadProblem({triggerRefresh: true, withLoader: false})
    }
  }

  public archiveProblem = async () => {
    await APIService.archiveProblem(this.problemId)
    if (this.problem.value != null) {
      services.getDispatchService().dispatch('problemArchived', this.problem.value)
    }
  }

  public validateProblem = async () => {
    const response = await APIService.validateProblem(this.problemId)
    if (response.ok) {
      this.problem.publish(response.body)
    }
  }
}

type SelectorInput = {
  token: string
  problemId: string | number
}

const tokenSelector = (input: SelectorInput) => input.token
const problemIdSelector = (input: SelectorInput) => input.problemId.toString()

const cache = new Cache()

export const problemSolutionServiceSelector = selector(tokenSelector, problemIdSelector, (token, problemId) => {
  const cacheKey = `${token}_${problemId}`
  const service = cache.get<SingleProblemService>(cacheKey) ?? new SingleProblemService(problemId)
  cache.set(cacheKey, service, '5min')
  return service
})
